import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material';
import { ConfigurationService } from './impl/configuration/configuration.service';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import {
  DialogAction,
  DialogActionConfig,
  DialogConfig,
  DialogKey, DialogsConfig, DialogsConfiguration
} from '../../shared/components/dialogs/dialogs-configuration';
import { IframeCommunicationService } from '../../shared/services/iframe-communication.service';
import { noop } from '../utils/utils';
import {AssetsService} from '../../shared/services/assets.service';
import {NativeIframeCommunicationService} from '../../shared/services/native-iframe-communication.service';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  private config: DialogsConfig;
  private iframeBehaviourInitialized = false;

  constructor(private dialog: MatDialog,
              private iframeService: IframeCommunicationService,
              private nativeIframeCommunicationService: NativeIframeCommunicationService,
              private configuration: ConfigurationService,
              private assetsService: AssetsService) {
    this.config = new DialogsConfiguration(this.configuration, this.assetsService).config;
    this.initIframeBehaviour();
  }

  get openedDialogsExist(): boolean {
    return !!this.dialog.openDialogs.length;
  }

  get(key: DialogKey): DialogConfig {
    return this.config[key];
  }

  isOpened(key: DialogKey): boolean {
    return this.config[key] ? this.config[key].isOpened : false;
  }

  open(key: DialogKey, customConfig?: any): void {
    if (customConfig) {
      this.updateDialogData(key, customConfig);
    }
    if (!this.config[key].isOpened) {
      this.config[key].dialog = this.dialog.open(this.config[key].component, this.getConfig(key));
      this.config[key].isOpened = true;
      this.attachAfterClosed(key);
    }
  }

  close(key: DialogKey, result?: DialogAction): void {
    if (this.config[key] && this.config[key].isOpened) {
      this.config[key].dialog.close(result);
      this.config[key].dialog = null;
      this.config[key].isOpened = false;
    }
  }

  addAction(key: DialogKey, action: DialogActionConfig): void {
    if (!isNotNullOrUndefined(this.config[key].actions)) {
      this.config[key].actions = [];
    }
    this.config[key].actions.push(action);
  }

  closeOpenedDialogs(): void {
    this.dialog.openDialogs.forEach((dialog: MatDialogRef<any>) => {
      if (isNotNullOrUndefined(dialog.componentInstance.data)) {
        const {canCloseOnNavigation} = dialog.componentInstance.data;
        if (!isNotNullOrUndefined(canCloseOnNavigation) || canCloseOnNavigation) {
          dialog.close();
        }
      } else {
        dialog.close();
      }
    });
  }

  private updateDialogData(key: DialogKey, config: any): void {
    this.config[key].dialogData = {
      ...this.config[key].dialogData,
      ...config
    };
  }

  private getConfig(key: DialogKey): MatDialogConfig {
    const dialogData = this.get(key).dialogData;
    const configBuilder = this.get(key).component.getConfigBuilder ? this.get(key).component.getConfigBuilder() : null;
    return configBuilder ? configBuilder.buildConfiguration(dialogData) : null;
  }

  private attachAfterClosed(key: DialogKey): void {
    if (isNotNullOrUndefined(this.config[key].dialog)) {
      this.config[key].dialog.afterClosed().subscribe(result => {
        this.config[key].isOpened = false;
        this.handleAction(key, result);
      });
    }
  }

  private handleAction(key: DialogKey, actionKey: DialogAction): void {
    if (isNotNullOrUndefined(this.config[key].actions)) { // dialog has action(s)
      if (isNotNullOrUndefined(actionKey)) { // close result is defined
        const actionConfig = this.config[key].actions.find(config => config.actionKey === actionKey);
        this.performAction(actionConfig);
      } else { // close result is undefined - perform default action (if exists)
        const defaultActionConfig = this.config[key].actions.find(config => config.isDefault);
        this.performAction(defaultActionConfig);
      }
    }
  }

  private performAction(actionConfig: DialogActionConfig): void {
    if (isNotNullOrUndefined(actionConfig)) {
      actionConfig.callback();
    }
  }

  private initIframeBehaviour(): void {
    this.iframeBehaviourInitialized = true;
    this.dialog.afterOpened.subscribe(event => {
      if (this.iframeService.pageInfoReadySubject.getValue()) { // page info is ready
        this.iframeService.onModalOpen();
      } else { // wait for first page info
        this.iframeService.pageInfoReadySubject.subscribe(ready => {
          ready ? this.iframeService.onModalOpen() : noop();
        });
      }
      this.nativeIframeCommunicationService.onModalOpen();
    });
    this.dialog.afterAllClosed.subscribe(() => {
      this.iframeService.onModalClose();
      this.nativeIframeCommunicationService.onModalClose();
    });
  }

}
